import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, EyebrowSizeField } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

const CATEGORIES = ['SPORTS', 'MEDIA', 'INVESTMENT', 'INNOVATION', 'WELLNESS', 'HOSPITALITY', 'ACADEMIA'];

export default function SpeakersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sectionMeta, setSectionMeta] = useState({});
  const [savedMeta, setSavedMeta] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/speakers');
    setItems(r.data || []);
    const s = await api.get('/admin/settings');
    setSectionMeta((s.data?.sections_meta || {}).speakers || {});
  };
  useEffect(() => { load(); }, []);

  const addNew = () => setEditing({ name: '', title: '', categories: [], photo: '', linkedin: '', bio: '', visible: true, order: items.length, session_info: '', linkedin_url: '', twitter_url: '', instagram_url: '', website_url: '', show_bio: true, show_session_info: true, show_social_links: true, slug: '' });

  const remove = async (id) => {
    if (!window.confirm('Delete this speaker?')) return;
    await api.delete(`/admin/speakers/${id}`);
    load();
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    await api.put('/admin/speakers/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
  };

  const toggleCat = (cat) => {
    const cats = editing.categories || [];
    setEditing({ ...editing, categories: cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat] });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        title: editing.title,
        categories: editing.categories || [],
        photo: editing.photo || '',
        linkedin: editing.linkedin || '',
        bio: editing.bio || '',
        visible: editing.visible !== false,
        order: typeof editing.order === 'number' ? editing.order : items.length,
        session_info: editing.session_info || '',
        linkedin_url: editing.linkedin_url || '',
        twitter_url: editing.twitter_url || '',
        instagram_url: editing.instagram_url || '',
        website_url: editing.website_url || '',
        show_bio: editing.show_bio !== false,
        show_session_info: editing.show_session_info !== false,
        show_social_links: editing.show_social_links !== false,
        slug: editing.slug || '',
      };
      if (editing.id) {
        await api.put(`/admin/speakers/${editing.id}`, payload);
      } else {
        await api.post('/admin/speakers', payload);
      }
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const setMeta = (k, v) => setSectionMeta({ ...sectionMeta, [k]: v });

  // v3.15 — Eyebrow size commit handler (see ExperiencesTab for full contract).
  const commitEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...sectionMeta };
      delete next.eyebrow_size;
      setSectionMeta(next);
    } else {
      setSectionMeta({ ...sectionMeta, eyebrow_size: n });
    }
  };
  const saveMeta = async () => {
    const all = await api.get('/admin/settings');
    const next = { ...(all.data?.sections_meta || {}), speakers: { ...sectionMeta } };
    await api.put('/admin/settings/sections-meta', next);
    setSavedMeta(true); setTimeout(() => setSavedMeta(false), 2000);
  };

  return (
    <div>
      <Section title="Section Labels">
        <TextField label="Eyebrow" value={sectionMeta.eyebrow} onChange={(v) => setMeta('eyebrow', v)} />
        <TextField label="Headline" value={sectionMeta.headline} onChange={(v) => setMeta('headline', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="View All Button Label" value={sectionMeta.button_label} onChange={(v) => setMeta('button_label', v)} />
          <TextField label="View All Button URL" value={sectionMeta.button_url} onChange={(v) => setMeta('button_url', v)} />
        </div>
        <EyebrowSizeField
          value={sectionMeta.eyebrow_size}
          onCommit={commitEyebrowSize}
          testid="speakers-eyebrow-size"
          hint="Controls only the orange EYEBROW label above the Speakers headline."
        />
        <div className="mt-3 flex items-center gap-3">
          <button onClick={saveMeta} className="btn-primary text-[12px] !py-2 !px-4" data-testid="save-speakers-meta">Save Section Labels</button>
          {savedMeta && <span className="font-body text-[12px] text-green-700">Saved ✓</span>}
        </div>
      </Section>

      <Section title="Speakers" action={<button onClick={addNew} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-speaker"><Plus size={14} className="mr-1" /> Add Speaker</button>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-bg-canvas shrink-0">
                {it.photo && <img src={it.photo} alt={it.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish">{it.name}</div>
                <div className="font-body text-[12px] text-ink-muted line-clamp-1">{it.title}</div>
                <div className="font-body text-[10px] text-ink-soft uppercase mt-0.5">{(it.categories || []).join(', ')}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => setEditing({ ...it })} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">{editing.id ? 'Edit Speaker' : 'New Speaker'}</h3>
            <ImageUploader value={editing.photo} onChange={(v) => setEditing({ ...editing, photo: v })} label="Photo" />
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <TextField label="Title / Organization" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
            </div>
            <div className="mt-4">
              <label className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-2">Categories</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCat(c)}
                    className={'font-body font-semibold text-[11px] uppercase tracking-wider2 px-3 py-1.5 rounded-full border ' + ((editing.categories || []).includes(c) ? 'bg-brand-orange text-white border-brand-orange' : 'border-border-line text-ink-primary')}>{c}</button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="LinkedIn URL (legacy)" value={editing.linkedin} onChange={(v) => setEditing({ ...editing, linkedin: v })} />
              <Toggle label="Visible" value={editing.visible !== false} onChange={(v) => setEditing({ ...editing, visible: v })} />
            </div>
            <div className="mt-4">
              <TextField label="Bio" value={editing.bio} onChange={(v) => setEditing({ ...editing, bio: v })} multiline />
            </div>

            {/* === v3.4: detail page fields === */}
            <div className="mt-6 pt-6 border-t border-border-line">
              <h4 className="font-display font-bold uppercase text-ink-primary text-[13px] tracking-tightish">Detail Page Content</h4>
              <p className="mt-1 font-body text-[11px] text-ink-muted">These fields render on the public /speakers/&lt;slug&gt; detail page.</p>

              <div className="mt-4">
                <label className="block">
                  <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">Session Info</span>
                  <textarea
                    value={editing.session_info || ''}
                    onChange={(e) => setEditing({ ...editing, session_info: e.target.value })}
                    rows={4}
                    className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-white focus:outline-none focus:border-ink-primary"
                    data-testid="speaker-edit-session-info"
                  />
                </label>
                <p className="mt-1 font-body text-[11px] text-ink-muted">Talk title, date/time, room, or any session-related notes.</p>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="LinkedIn URL" value={editing.linkedin_url} onChange={(v) => setEditing({ ...editing, linkedin_url: v })} placeholder="https://linkedin.com/in/..." testid="speaker-edit-linkedin-url" />
                <TextField label="X (Twitter) URL" value={editing.twitter_url} onChange={(v) => setEditing({ ...editing, twitter_url: v })} placeholder="https://x.com/..." testid="speaker-edit-twitter-url" />
                <TextField label="Instagram URL" value={editing.instagram_url} onChange={(v) => setEditing({ ...editing, instagram_url: v })} placeholder="https://instagram.com/..." testid="speaker-edit-instagram-url" />
                <TextField label="Website URL" value={editing.website_url} onChange={(v) => setEditing({ ...editing, website_url: v })} placeholder="https://example.com" testid="speaker-edit-website-url" />
              </div>

              <div className="mt-5">
                <h5 className="font-display font-bold uppercase text-ink-primary text-[12px] tracking-tightish">Display Settings</h5>
                <div className="mt-3 space-y-2">
                  <Toggle label="Show bio on detail page" value={editing.show_bio !== false} onChange={(v) => setEditing({ ...editing, show_bio: v })} testid="speaker-edit-show-bio" />
                  <Toggle label="Show session info on detail page" value={editing.show_session_info !== false} onChange={(v) => setEditing({ ...editing, show_session_info: v })} testid="speaker-edit-show-session" />
                  <Toggle label="Show social links on detail page" value={editing.show_social_links !== false} onChange={(v) => setEditing({ ...editing, show_social_links: v })} testid="speaker-edit-show-social" />
                </div>
                <p className="mt-2 font-body text-[11px] text-ink-muted">Uncheck to hide that section on this speaker&apos;s public detail page. Useful if a speaker hasn&apos;t provided that info yet.</p>
              </div>

              {editing.id && (
                <div className="mt-5">
                  <label className="block">
                    <span className="block font-body text-[12px] font-semibold uppercase text-ink-muted tracking-wider2 mb-1.5">URL Slug</span>
                    <input
                      type="text"
                      value={editing.slug || ''}
                      readOnly
                      className="w-full border border-border-line rounded-sm px-3 py-2 font-body text-[14px] bg-bg-canvas text-ink-muted cursor-not-allowed"
                      data-testid="speaker-edit-slug"
                    />
                  </label>
                  <p className="mt-1 font-body text-[11px] text-ink-muted">Auto-generated from name. Will be /speakers/&lt;slug&gt;.</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
